import envService from '@/services/env.service';
import * as amplitude from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import { PropsWithChildren, createContext, useContext } from 'react';

type AmplitudeValue = any;

const AmplitudeContext = createContext<AmplitudeValue>(amplitude);

export function useAmplitude() {
  const context = useContext(AmplitudeContext);

  if (!context) throw new Error('useAmplitude must be used within a AmplitudeProvider');

  return context;
}

function getDeviceIdFromUrlOrCookie(): string | undefined {
  // Priority 1: URL parameter (from marketing site)
  const urlParams = new URLSearchParams(window.location.search);
  const urlDeviceId = urlParams.get('amp_device_id');
  if (urlDeviceId) {
    // Store in cookie for future page loads with matching domain configuration
    // Use .catchbio.com domain in production for cross-subdomain sharing
    const isProduction = window.location.hostname.endsWith('.catchbio.com');
    const domainAttr = isProduction ? '; domain=.catchbio.com' : '';
    document.cookie = `amp_device_id=${urlDeviceId}; path=/; max-age=${60 * 60 * 24 * 365}; secure; samesite=lax${domainAttr}`;
    return urlDeviceId;
  }

  // Priority 2: Cookie (cross-domain or previously stored)
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'amp_device_id') {
      return value;
    }
  }

  // Priority 3: Let Amplitude SDK generate new ID
  return undefined;
}

export function AmplitudeProvider({ children }: PropsWithChildren) {
  // Read device ID from URL parameter or cookie
  const deviceId = getDeviceIdFromUrlOrCookie();

  // Create and Install Session Replay Plugin
  const amplitudeSampleRate = envService.getEnv().VITE_AMPLITUDE_SAMPLE_RATE;
  // If the sample rate is not set, use remote configuration or default is 0
  const sampleRate = amplitudeSampleRate && amplitudeSampleRate !== '' ? Number(amplitudeSampleRate) : undefined;
  const sessionReplayTracking = sessionReplayPlugin({
    sampleRate,
    // Use proxy URL if configured, empty string falls back to Amplitude default
    trackServerUrl: envService.getEnv().VITE_AMPLITUDE_SR_PROXY_URL || '',
  });
  amplitude.add(sessionReplayTracking);

  amplitude.init(envService.getEnv().VITE_AMPLITUDE_API_KEY, {
    deviceId: deviceId,
    autocapture: {
      attribution: {
        resetSessionOnNewCampaign: true,
      },
      pageViews: false,
      sessions: true,
      formInteractions: false,
      fileDownloads: false,
      elementInteractions: false,
    },
    // Use proxy URL if configured, empty string falls back to Amplitude default
    serverUrl: envService.getEnv().VITE_AMPLITUDE_PROXY_URL || '',
  });

  return <AmplitudeContext.Provider value={amplitude}>{children}</AmplitudeContext.Provider>;
}
